import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ReactPlayer from 'react-player';
import { useNavigate, useParams } from 'react-router-dom'; // 引入 useParams 来获取路由参数
import { incrementPlayCount, fetchComments, postComment } from '../api'; // 假设你有这些API函数

// 定义全局样式变量
const theme = {
  backgroundColor: '#F0F8FF', // 使用浅蓝色作为背景色，偏中性且清新
  primaryColor: '#4CAF50', // 清新的绿色，代表生机与活力
  secondaryColor: '#FFD700', // 明亮的金色（未在当前示例中使用）
  fontColor: '#333333', // 深灰色字体，确保可读性
  cardBackgroundColor: '#FFFFFF', // 卡片背景色，白色更显清新
  commentBoxColor: '#F9F9F9', // 评论框背景色，稍微浅一点的白色
};

// 样式化电影卡片容器
const MovieCard = styled.div`
  background-color: ${theme.cardBackgroundColor};
  color: ${theme.fontColor};
  margin: 15px;
  padding: 20px;
  border-radius: 15px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.02);
  }
`;

// 样式化电影标题
const MovieTitle = styled.h2`
  font-size: 1.8em;
  text-align: center;
  color: ${theme.primaryColor};
  margin-bottom: 15px;
`;

// 样式化电影详情
const MovieDetails = styled.p`
  font-size: 1em;
  text-align: center;
  margin: 5px 0;
`;

// 样式化播放器包裹器
const PlayerWrapper = styled.div`
  width: 100%;
  position: relative;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  height: 0;
  cursor: pointer; /* 添加指针样式表示可点击 */
  border-radius: 10px;
  overflow: hidden;
`;

// 样式化评论部分
const CommentSection = styled.div`
  margin-top: 20px;
  padding: 10px;
  background-color: ${theme.commentBoxColor};
  border-radius: 10px;
`;

// 样式化提交按钮
const SubmitButton = styled.button`
  padding: 10px 20px;
  background-color: ${theme.primaryColor};
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1em;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: darken(${theme.primaryColor}, 10%);
  }
`;

function MovieCardComponent({ movie, showComments = false }) {
  const navigate = useNavigate();
  const { id } = useParams(); // 获取路由参数中的 ID
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [playbackError, setPlaybackError] = useState(false); // 新增状态用于跟踪播放错误
  const [retryCount, setRetryCount] = useState(0); // 新增状态用于跟踪重试次数
  const [isRetrying, setIsRetrying] = useState(false); // 新增状态用于跟踪是否正在重试

  const isDetailPage = !!id && id === movie.id.toString(); // 判断是否在详细页面

  const handlePlay = async () => {
    try {
      await incrementPlayCount(movie.id); // 增加播放次数
      navigate(`/videos/${movie.id}`); // 导航到视频详情页
    } catch (error) {
      console.error('Failed to increment play count:', error);
    }
  };

  useEffect(() => {
    if (showComments && movie.id) {
      setLoadingComments(true);
      fetchComments(movie.id)
        .then(setComments)
        .catch(console.error)
        .finally(() => setLoadingComments(false));
    }
  }, [movie.id, showComments]);

  const handleCommentSubmit = async (event) => {
    event.preventDefault();
    if (!newComment.trim()) return;

    try {
      await postComment(movie.id, { text: newComment });
      // 刷新当前电影数据以包含新评论
      const updatedComments = await fetchComments(movie.id);
      setComments(updatedComments);
      setNewComment(''); // 清空输入框
    } catch (error) {
      console.error('Error posting comment:', error);
    }
  };

  // 自动重新加载资源
  useEffect(() => {
    if (playbackError && retryCount < 8 && !isRetrying) { // 设置最大重试次数为8次
      setIsRetrying(true);
      const timer = setTimeout(() => {
        setRetryCount(prevCount => prevCount + 1);
        setPlaybackError(false); // 重置错误状态以便再次尝试加载
        setIsRetrying(false);
      }, 5000); // 每隔5秒尝试重新加载

      return () => clearTimeout(timer);
    }
  }, [playbackError, retryCount, isRetrying]);

  return (
    <MovieCard>
      <MovieTitle>{movie.title}</MovieTitle>
      <PlayerWrapper onClick={handlePlay}>
        <ReactPlayer 
          url={movie.path} 
          controls={true}
          playing={false}
          width="100%"
          height="100%"
          config={{
            file: {
              attributes: {
                preload: isDetailPage ? 'auto' : 'none', // 在详细页面预加载，在其他页面不预加载
              },
            },
          }}
          onError={() => {
            setPlaybackError(true); // 设置播放错误状态
            console.log(`Video load failed, retrying... Attempt ${retryCount + 1}`);
          }}
          style={{ position: 'absolute', top: '0', left: '0' }}
        />
      </PlayerWrapper>
      <MovieDetails>分类: {movie.category?.name}</MovieDetails>
      <MovieDetails>描述: {movie.description}</MovieDetails>
      <MovieDetails>推荐理由: {movie.recommendedReason}</MovieDetails>
      <MovieDetails>播放次数: {movie.playCount}</MovieDetails>
      <MovieDetails>上传时间: {new Date(movie.uploadTime).toLocaleString()}</MovieDetails>

      {/* 如果 showComments 为 true，则显示评论 */}
      {showComments && (
        <>
          <CommentSection>
            <h3>评论:</h3>
            {loadingComments ? (
              <p>Loading comments...</p>
            ) : comments.length > 0 ? (
              comments.map(comment => (
                <p key={comment.id}>{comment.text}</p>
              ))
            ) : (
              <p>No comments yet.</p>
            )}
          </CommentSection>

          {/* 添加评论表单 */}
          <form onSubmit={handleCommentSubmit}>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="评论..."
              rows="4"
              style={{
                width: '100%',
                marginBottom: '10px',
                padding: '10px',
                borderRadius: '5px',
                border: '1px solid #ccc',
              }}
            />
            <SubmitButton type="submit">提交评论</SubmitButton>
          </form>
        </>
      )}

      {/* 显示重试信息 */}
      {playbackError && retryCount < 8 && (
        <p>视频加载失败，正在重试... 尝试次数：{retryCount + 1}/8</p>
      )}
      {retryCount >= 8 && (
        <p>视频加载失败，已达到最大重试次数，请检查网络连接或稍后再试。</p>
      )}
    </MovieCard>
  );
}

export default MovieCardComponent;